import React, { useEffect, useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, FileText, CheckSquare, Tag, X, AlertTriangle, ExternalLink, Clock, Database, Sparkles, Target, Lightbulb, MessageSquare, Zap, Heart, TrendingUp, Copy, Check } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { zohoCrmService } from '../services/zohoCrmService';
const steps = [{
  id: 1,
  name: 'Audience',
  icon: Users
}, {
  id: 2,
  name: 'Content',
  icon: FileText
}, {
  id: 3,
  name: 'Review',
  icon: CheckSquare
}, {
  id: 4,
  name: 'Export',
  icon: Database
}];
interface MarketingStrategy {
  overview: string;
  subjectLines: string[];
  keyMessages: string[];
  callToActions: string[];
  emotionalTriggers: string[];
  timingTips: string[];
  personalizationTips: string[];
}
// Smart strategy generator based on audience analysis
function generateMarketingStrategy(audienceDescription: string): MarketingStrategy {
  const desc = audienceDescription.toLowerCase();
  // Detect audience characteristics
  const isPastors = desc.includes('pastor') || desc.includes('church leader') || desc.includes('ministry');
  const isYouth = desc.includes('young') || desc.includes('youth') || desc.includes('teen') || desc.includes('millennial') || desc.includes('gen z');
  const isParents = desc.includes('parent') || desc.includes('family') || desc.includes('mom') || desc.includes('dad') || desc.includes('children');
  const isInfluencers = desc.includes('influencer') || desc.includes('creator') || desc.includes('blogger') || desc.includes('social media');
  const isNewViewers = desc.includes('new') || desc.includes('first time') || desc.includes("haven't watched") || desc.includes('curious');
  const isFans = desc.includes('fan') || desc.includes('love') || desc.includes('dedicated') || desc.includes('loyal') || desc.includes('supporter');
  const isDonors = desc.includes('donor') || desc.includes('support') || desc.includes('give') || desc.includes('contribute');
  const isInternational = desc.includes('international') || desc.includes('global') || desc.includes('brazil') || desc.includes('latin') || desc.includes('spanish');
  const isEventFocused = desc.includes('event') || desc.includes('screening') || desc.includes('premiere') || desc.includes('gathering');
  const isVolunteers = desc.includes('volunteer') || desc.includes('ambassador') || desc.includes('help') || desc.includes('serve');
  let strategy: MarketingStrategy = {
    overview: '',
    subjectLines: [],
    keyMessages: [],
    callToActions: [],
    emotionalTriggers: [],
    timingTips: [],
    personalizationTips: []
  };
  // Generate tailored strategy based on detected characteristics
  if (isPastors) {
    strategy = {
      overview: 'Focus on ministry impact and community transformation. Pastors respond to content that helps them serve their congregations better and creates meaningful discussion opportunities.',
      subjectLines: ['A resource your congregation will thank you for', 'How The Chosen is sparking faith conversations', 'Free discussion guides for your small groups', 'What pastors are saying about Season [X]', '[Name], bring The Chosen to your church this month'],
      keyMessages: ['Highlight free church licensing and screening resources', 'Share testimonials from other pastors about congregation impact', 'Emphasize discussion guides and sermon tie-in materials', 'Focus on how the show sparks faith conversations', 'Mention community building and outreach opportunities'],
      callToActions: ['Download Free Church Resources', 'Host a Screening Event', 'Get Discussion Guides', 'Join the Pastor Network', 'Request a Church License'],
      emotionalTriggers: ["Shepherd's heart - helping your flock grow", 'Community impact and transformation stories', 'Equipping for ministry effectiveness', 'Being part of a movement larger than one church'],
      timingTips: ['Send Tuesday-Wednesday when pastors plan weekend services', 'Avoid Monday (recovery day) and Friday-Saturday (prep time)', 'Time for early morning (5-7am) when many pastors have devotional time', 'Consider sending 6-8 weeks before major church seasons (Easter, Christmas)'],
      personalizationTips: ['Use their name and church name if available', 'Reference their region or denomination if known', "Acknowledge their role: 'As a shepherd of your community...'", 'Segment by church size for relevant resource recommendations']
    };
  } else if (isYouth) {
    strategy = {
      overview: "Use authentic, relatable language without being 'try-hard.' Young audiences value genuineness, social proof, and content that respects their intelligence while being visually engaging.",
      subjectLines: ["This isn't your grandma's Bible story", "Why millions your age can't stop watching", "The show that's actually worth your time", 'Your friends are already talking about this', 'Finally, something worth binging'],
      keyMessages: ['Lead with social proof (millions of viewers, viral moments)', 'Highlight the cinematic quality and production value', 'Focus on relatable character struggles and growth', "Emphasize it's free and accessible on multiple platforms", 'Share behind-the-scenes and cast content'],
      callToActions: ['Start Watching Free', "See What Everyone's Talking About", 'Watch the Trailer', 'Join the Community', 'Share with Friends'],
      emotionalTriggers: ['FOMO - being part of a cultural moment', 'Authenticity and realness of characters', 'Finding meaning and purpose', 'Community and belonging'],
      timingTips: ["Send evenings (7-10pm) or weekends when they're relaxing", 'Avoid school/work hours (9am-3pm weekdays)', "Consider Sunday evenings for 'what to watch this week' angle", 'Align with new episode releases for maximum engagement'],
      personalizationTips: ['Use casual, conversational tone', 'Include GIFs or dynamic content if platform allows', 'Reference trending topics or memes when appropriate', 'Segment by platform preference (YouTube, app, etc.)']
    };
  } else if (isParents) {
    strategy = {
      overview: 'Parents are looking for quality content they can share with their families. Emphasize safety, values, and the opportunity for meaningful family discussions.',
      subjectLines: ['Finally, a show the whole family can watch together', "Screen time you'll actually feel good about", 'Start a faith conversation with your kids tonight', 'The show families are watching together', '[Name], your family movie night just got better'],
      keyMessages: ['Emphasize family-friendly content with no inappropriate scenes', 'Highlight discussion opportunities with children', 'Share how other families are using the show', 'Focus on bringing the Bible to life in an accessible way', "Mention it's free - no subscription needed"],
      callToActions: ['Plan Your Family Watch Night', 'Get the Family Discussion Guide', 'Watch Together This Weekend', 'Start with Episode 1', 'Download the Free App'],
      emotionalTriggers: ['Quality time with family', 'Passing on faith to the next generation', 'Creating lasting memories together', 'Peace of mind about content quality'],
      timingTips: ['Send Thursday-Friday for weekend planning', 'Early evening (5-7pm) when parents are thinking about family time', 'Avoid hectic morning hours', 'Consider school breaks and holidays for binge-watching campaigns'],
      personalizationTips: ['Acknowledge the challenge of finding good family content', "Segment by children's ages if known", 'Reference family-oriented values', 'Include testimonials from other parents']
    };
  } else if (isInfluencers) {
    strategy = {
      overview: 'Influencers need exclusive access, shareable content, and clear value for their audience. Focus on partnership opportunities and unique content they can share.',
      subjectLines: ['Exclusive: Be first to share this with your audience', 'Your followers are asking about The Chosen', 'Partner with the #1 crowdfunded show ever', 'Content your audience will thank you for', 'Join 500+ creators sharing The Chosen'],
      keyMessages: ['Offer exclusive early access or behind-the-scenes content', 'Provide ready-to-share assets and talking points', "Highlight the show's organic viral success", 'Emphasize authentic partnership (not forced promotion)', 'Share engagement stats and audience response data'],
      callToActions: ['Join the Creator Program', 'Get Exclusive Assets', 'Request Early Access', 'Download Press Kit', 'Schedule a Partnership Call'],
      emotionalTriggers: ['Being part of something groundbreaking', 'Providing value to their audience', 'Authentic alignment with their values', 'Exclusive access and insider status'],
      timingTips: ['Send mid-week when planning content calendars', 'Align with new season/episode announcements', 'Give 2-3 weeks lead time for content creation', 'Avoid major platform algorithm change periods'],
      personalizationTips: ['Reference their platform and audience size', 'Acknowledge their content style and niche', 'Offer customized partnership tiers', 'Include specific engagement opportunities']
    };
  } else if (isDonors) {
    strategy = {
      overview: 'Donors want to see impact and feel connected to the mission. Focus on transparency, gratitude, and the tangible difference their support makes.',
      subjectLines: ['See the impact you made possible', 'You helped bring this story to [X] million people', '[Name], thank you for making this possible', "Your gift is changing lives - here's proof", "A personal update on what you're supporting"],
      keyMessages: ['Lead with gratitude and acknowledgment', 'Share specific impact metrics and stories', 'Highlight viewer testimonials and life changes', 'Be transparent about how funds are used', 'Show the global reach their support enables'],
      callToActions: ['See Your Impact', 'Read Viewer Stories', 'Continue Your Support', 'Share the Mission', 'Join the Founding Member Circle'],
      emotionalTriggers: ['Being part of something bigger than themselves', 'Seeing tangible results from their generosity', 'Personal connection to the mission', 'Legacy and lasting impact'],
      timingTips: ['Send impact reports monthly or quarterly', 'Year-end giving season (November-December) for appeals', 'After major milestones (new season, viewer records)', 'Avoid asking too frequently - balance with gratitude'],
      personalizationTips: ['Reference their giving history and total impact', 'Use their name and acknowledge their specific contribution', 'Segment by giving level for appropriate asks', 'Include handwritten or personal elements when possible']
    };
  } else if (isNewViewers) {
    strategy = {
      overview: 'New viewers need a low-barrier entry point and social proof. Remove friction, address common objections, and make starting easy.',
      subjectLines: ['Curious about The Chosen? Start here', 'Why 770+ million people have watched this show', "It's free, it's good, and here's where to start", "The show everyone's recommending - see why", 'Your friends were right about this one'],
      keyMessages: ["Emphasize it's completely free - no catch", 'Provide a clear starting point (Episode 1, Season 1)', 'Share impressive viewership numbers as social proof', "Address the 'religious show' hesitation with quality focus", 'Highlight critical acclaim and production value'],
      callToActions: ['Watch Episode 1 Free', 'Start Your Journey', 'See What the Hype Is About', 'Watch the First 10 Minutes', 'Download the Free App'],
      emotionalTriggers: ['Curiosity and discovery', 'Not wanting to miss out on something good', 'Finding unexpected quality', 'Joining a community of viewers'],
      timingTips: ['Weekend evenings when looking for something to watch', 'After major press coverage or viral moments', 'Holiday seasons when people have more free time', 'New season launches for fresh entry points'],
      personalizationTips: ['Reference how they heard about the show if known', 'Acknowledge their curiosity without being pushy', 'Offer multiple viewing platform options', "Provide 'what to expect' context"]
    };
  } else if (isFans) {
    strategy = {
      overview: 'Dedicated fans want deeper engagement, exclusive content, and ways to share their passion. Reward their loyalty and give them insider access.',
      subjectLines: ['For our biggest fans: exclusive first look', "You've earned this - insider access inside", "The behind-the-scenes content you've been asking for", 'Thank you for being part of this journey', "[Name], you're one of our most dedicated viewers"],
      keyMessages: ['Offer exclusive behind-the-scenes content', 'Provide early access to announcements and trailers', 'Create opportunities to connect with cast/crew', 'Give them shareable content to spread the word', "Acknowledge their role in the show's success"],
      callToActions: ['Get Exclusive Access', 'Join the Inner Circle', 'Share Your Story', 'Invite Friends to Watch', 'Shop Official Merchandise'],
      emotionalTriggers: ['Feeling valued and recognized', 'Being an insider', 'Pride in supporting the show', 'Community with fellow fans'],
      timingTips: ['Immediately after new episode releases', 'During production for behind-the-scenes content', 'Anniversary dates and milestones', 'Before major announcements for early access'],
      personalizationTips: ['Reference their viewing history if available', "Acknowledge how long they've been a fan", 'Offer tiered benefits based on engagement', 'Include fan community highlights']
    };
  } else if (isInternational) {
    strategy = {
      overview: 'International audiences need localized content and cultural relevance. Emphasize accessibility, dubbing/subtitles, and local community connections.',
      subjectLines: ['The Chosen is now available in your language', 'Join millions watching in [Region]', 'Your community is discovering The Chosen', 'Watch in [Language] - completely free', 'A global movement, local impact'],
      keyMessages: ['Highlight available languages and subtitles', 'Share regional viewership numbers and growth', 'Feature testimonials from their region', 'Emphasize free access regardless of location', 'Mention local screening events or communities'],
      callToActions: ['Watch in Your Language', 'Find Local Screenings', 'Join Your Regional Community', 'Download with Subtitles', 'Share with Your Community'],
      emotionalTriggers: ['Cultural pride and representation', 'Global community connection', 'Accessibility and inclusion', 'Local impact and relevance'],
      timingTips: ['Consider local time zones for send times', 'Align with regional holidays and observances', 'Coordinate with dubbed version releases', 'Account for cultural calendar differences'],
      personalizationTips: ['Use their preferred language', 'Reference their specific country/region', 'Include locally relevant testimonials', 'Adapt imagery and examples to their culture']
    };
  } else {
    // Default general strategy
    strategy = {
      overview: "Focus on the universal appeal of authentic storytelling and the unique nature of The Chosen as a free, high-quality production that's changing how people experience biblical narratives.",
      subjectLines: ['See why 770+ million have watched The Chosen', "The show that's changing everything - watch free", 'Your next favorite show is waiting', 'Join the movement millions are talking about', 'Something special is happening - be part of it'],
      keyMessages: ['Lead with the unique value proposition (free, high-quality, authentic)', 'Use social proof (viewership numbers, testimonials)', 'Highlight the human stories and relatable characters', 'Emphasize accessibility across all platforms', 'Share the grassroots, crowdfunded origin story'],
      callToActions: ['Watch Free Now', 'Start Episode 1', 'Download the App', 'Join the Community', 'Share with Friends'],
      emotionalTriggers: ['Curiosity about what millions are watching', 'Desire for meaningful, quality content', 'Connection to timeless stories', 'Being part of something bigger'],
      timingTips: ['Test different days - typically Tuesday-Thursday perform well', 'Evening sends (6-8pm local time) for entertainment content', 'Avoid major holidays unless content is holiday-themed', 'Align with new episode or season releases'],
      personalizationTips: ["Use recipient's name in subject line when possible", 'Segment by engagement level (new vs. returning)', 'Reference their location for regional relevance', 'Customize based on how they joined your list']
    };
  }
  return strategy;
}
export function CampaignBuilder() {
  const {
    currentWorkspace,
    newsletters,
    tags,
    subscriptions,
    createCampaign
  } = useWorkspace();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState<any>(null);
  // Marketing strategy state
  const [targetAudienceDescription, setTargetAudienceDescription] = useState('');
  const [generatedStrategy, setGeneratedStrategy] = useState<MarketingStrategy | null>(null);
  const [isGeneratingStrategy, setIsGeneratingStrategy] = useState(false);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [campaign, setCampaign] = useState({
    name: '',
    subject: '',
    preheader: '',
    fromName: `The Chosen ${currentWorkspace.name}`,
    fromEmail: `noreply@${currentWorkspace.id}.thechosen.tv`,
    targetNewsletters: [] as number[],
    targetTags: [] as string[],
    subRegions: [] as string[],
    htmlContent: '',
    textContent: '',
    status: 'draft' as const
  });
  const [sendBucketConfig, setSendBucketConfig] = useState({
    expiryHours: null as number | null,
    bucketId: ''
  });
  const [bucketConflicts, setBucketConflicts] = useState<any>(null);
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  useEffect(() => {
    if (currentStep === 4 && !sendBucketConfig.bucketId) {
      const bucketId = zohoCrmService.generateSendBucketId(currentWorkspace.id);
      setSendBucketConfig(prev => ({
        ...prev,
        bucketId
      }));
    }
  }, [currentStep, currentWorkspace.id, sendBucketConfig.bucketId]);
  useEffect(() => {
    if (currentStep === 4) {
      checkForConflicts();
    }
  }, [currentStep]);
  const handleNewsletterToggle = (id: number) => {
    setCampaign(prev => ({
      ...prev,
      targetNewsletters: prev.targetNewsletters.includes(id) ? prev.targetNewsletters.filter(n => n !== id) : [...prev.targetNewsletters, id]
    }));
  };
  const handleTagToggle = (tagName: string) => {
    setCampaign(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tagName) ? prev.targetTags.filter(t => t !== tagName) : [...prev.targetTags, tagName]
    }));
  };
  const handleSubRegionToggle = (region: string) => {
    setCampaign(prev => ({
      ...prev,
      subRegions: prev.subRegions.includes(region) ? prev.subRegions.filter(r => r !== region) : [...prev.subRegions, region]
    }));
  };
  const handleGenerateStrategy = () => {
    if (!targetAudienceDescription.trim()) return;
    setIsGeneratingStrategy(true);
    // Simulate a brief loading state for better UX
    setTimeout(() => {
      const strategy = generateMarketingStrategy(targetAudienceDescription);
      setGeneratedStrategy(strategy);
      setIsGeneratingStrategy(false);
    }, 800);
  };
  const handleCopyToClipboard = (text: string, itemId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemId);
    setTimeout(() => setCopiedItem(null), 2000);
  };
  const handleUseSubjectLine = (subjectLine: string) => {
    setCampaign(prev => ({
      ...prev,
      subject: subjectLine
    }));
  };
  const selectedNewsletters = newsletters.filter(n => campaign.targetNewsletters.includes(n.id));
  const totalRecipients = selectedNewsletters.reduce((sum, n) => sum + n.subscriberCount, 0);
  const getTargetEmails = (): string[] => {
    let targetSubs = subscriptions.filter(sub => campaign.targetNewsletters.includes(sub.listId || 0) && sub.status === 'confirmed');
    if (campaign.targetTags.length > 0) {
      targetSubs = targetSubs.filter(sub => sub.tags?.some(tag => campaign.targetTags.includes(tag)));
    }
    if (campaign.subRegions.length > 0) {
      targetSubs = targetSubs.filter(sub => !sub.subRegion || campaign.subRegions.includes(sub.subRegion));
    }
    return targetSubs.map(sub => sub.email);
  };
  const checkForConflicts = async () => {
    const emails = getTargetEmails();
    const result = await zohoCrmService.checkExistingBuckets(emails);
    setBucketConflicts(result);
    setShowConflictWarning(result.hasConflicts);
  };
  const handleExportToZoho = async () => {
    setIsExporting(true);
    try {
      const emails = getTargetEmails();
      const expiresAt = sendBucketConfig.expiryHours ? zohoCrmService.calculateExpiryDate(sendBucketConfig.expiryHours) : undefined;
      const result = await zohoCrmService.assignSendBucket(emails, {
        bucketId: sendBucketConfig.bucketId,
        expiresAt,
        contactCount: emails.length,
        workspaceId: currentWorkspace.id,
        campaignName: campaign.name
      });
      setExportResult(result);
      if (result.success) {
        createCampaign({
          ...campaign,
          workspaceId: currentWorkspace.id,
          status: 'exported',
          sendBucketId: sendBucketConfig.bucketId,
          sendBucketExpiresAt: expiresAt,
          sendBucketContactCount: result.updatedCount,
          exportedAt: new Date().toISOString(),
          recipientCount: result.updatedCount
        });
        setTimeout(() => navigate('/campaigns'), 2000);
      }
    } catch (error) {
      setExportResult({
        success: false,
        errors: [error instanceof Error ? error.message : 'Export failed']
      });
    } finally {
      setIsExporting(false);
    }
  };
  const getExpiryLabel = (hours: number | null) => {
    if (!hours) return 'No expiry';
    if (hours === 24) return '24 hours';
    if (hours === 48) return '48 hours';
    if (hours === 72) return '72 hours';
    return `${hours} hours`;
  };
  return <div className="space-y-8 max-w-4xl mx-auto" data-id="element-472">
      <div className="text-center" data-id="element-473">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-474">
          Create Campaign
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-475">
          Design and export audience to Zoho Campaigns.
        </p>
      </div>

      {/* Progress Bar */}
      <div className="relative" data-id="element-476">
        <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-477">
          <div className="w-full border-t border-gray-200" data-id="element-478" />
        </div>
        <div className="relative flex justify-between" data-id="element-479">
          {steps.map(step => {
          const isComplete = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          return <div key={step.id} className="flex flex-col items-center relative bg-white px-2" data-id="element-480">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${isComplete ? 'border-blue-600 bg-blue-600 text-white' : isCurrent ? 'border-blue-600 text-blue-600' : 'border-gray-300 text-gray-500'}`} data-id="element-481">
                  <step.icon className="h-5 w-5" data-id="element-482" />
                </div>
                <span className={`mt-2 text-xs font-medium ${isCurrent ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-483">
                  {step.name}
                </span>
              </div>;
        })}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8" data-id="element-484">
        {currentStep === 1 && <div className="space-y-6" data-id="element-485">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-486">
              Target Audience
            </h2>

            {currentWorkspace.subRegions.length > 0 && <div className="mb-6" data-id="element-487">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-488">
                  Target Sub-Regions
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-489">
                  {currentWorkspace.subRegions.map(region => <label key={region} className="flex items-center gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer" data-id="element-490">
                      <input type="checkbox" checked={campaign.subRegions.includes(region)} onChange={() => handleSubRegionToggle(region)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-491" />
                      <span className="text-sm text-gray-900" data-id="element-492">{region}</span>
                    </label>)}
                </div>
              </div>}

            <div data-id="element-493">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-494">
                Include Mailing Lists
              </label>
              <div className="space-y-2" data-id="element-495">
                {newsletters.map(newsletter => <label key={newsletter.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer" data-id="element-496">
                    <input type="checkbox" checked={campaign.targetNewsletters.includes(newsletter.id)} onChange={() => handleNewsletterToggle(newsletter.id)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-497" />
                    <div className="flex-1" data-id="element-498">
                      <div className="text-sm font-medium text-gray-900" data-id="element-499">
                        {newsletter.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-500">
                        {newsletter.subscriberCount} subscribers
                      </div>
                    </div>
                  </label>)}
              </div>
            </div>

            <div data-id="element-501">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-502">
                <Tag className="inline h-4 w-4 mr-1" data-id="element-503" />
                Filter by Tags (Optional)
              </label>
              <p className="text-xs text-gray-500 mb-2" data-id="element-504">
                Only include subscribers with these tags.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-505">
                {tags.map(tag => <button key={tag.id} type="button" onClick={() => handleTagToggle(tag.name)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border ${campaign.targetTags.includes(tag.name) ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'}`} data-id="element-506">
                    {campaign.targetTags.includes(tag.name) && <CheckCircle2 className="h-3 w-3" data-id="element-507" />}
                    {tag.name}
                  </button>)}
              </div>
              {campaign.targetTags.length > 0 && <p className="text-xs text-blue-600 mt-2" data-id="element-508">
                  Filtering to subscribers tagged with:{' '}
                  {campaign.targetTags.join(', ')}
                </p>}
            </div>
          </div>}

        {currentStep === 2 && <div className="space-y-6" data-id="element-509">
            {/* Marketing Strategy Generator */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6" data-id="element-510">
              <div className="flex items-start gap-4" data-id="element-511">
                <div className="p-3 bg-purple-100 rounded-lg" data-id="element-512">
                  <Sparkles className="h-6 w-6 text-purple-600" data-id="element-513" />
                </div>
                <div className="flex-1" data-id="element-514">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2" data-id="element-515">
                    Marketing Strategy Generator
                    <span className="text-xs font-normal px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full" data-id="element-516">
                      AI-Powered
                    </span>
                  </h3>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-517">
                    Describe your target audience and get tailored marketing
                    strategies, subject lines, and messaging recommendations.
                  </p>

                  <div className="mt-4" data-id="element-518">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-519">
                      <Target className="inline h-4 w-4 mr-1" data-id="element-520" />
                      Describe Your Target Audience
                    </label>
                    <textarea value={targetAudienceDescription} onChange={e => setTargetAudienceDescription(e.target.value)} placeholder="e.g., Young parents who are curious about The Chosen but haven't watched yet, pastors looking for church resources, dedicated fans who want exclusive content, international viewers in Brazil..." rows={3} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm" data-id="element-521" />
                    <p className="text-xs text-gray-500 mt-2" data-id="element-522">
                      Be specific! Include demographics, interests, relationship
                      with The Chosen, and goals.
                    </p>
                  </div>

                  <button onClick={handleGenerateStrategy} disabled={!targetAudienceDescription.trim() || isGeneratingStrategy} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-523">
                    {isGeneratingStrategy ? <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-524" />
                        Generating Strategy...
                      </> : <>
                        <Sparkles className="h-4 w-4" data-id="element-525" />
                        Generate Strategy
                      </>}
                  </button>
                </div>
              </div>

              {/* Generated Strategy Display */}
              {generatedStrategy && <div className="mt-6 space-y-6 border-t border-purple-200 pt-6" data-id="element-526">
                  {/* Overview */}
                  <div className="bg-white rounded-lg p-4 border border-purple-100" data-id="element-527">
                    <div className="flex items-center gap-2 text-purple-700 font-medium mb-2" data-id="element-528">
                      <Lightbulb className="h-4 w-4" data-id="element-529" />
                      Strategy Overview
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-530">
                      {generatedStrategy.overview}
                    </p>
                  </div>

                  {/* Subject Lines */}
                  <div className="bg-white rounded-lg p-4 border border-purple-100" data-id="element-531">
                    <div className="flex items-center gap-2 text-purple-700 font-medium mb-3" data-id="element-532">
                      <MessageSquare className="h-4 w-4" data-id="element-533" />
                      Recommended Subject Lines
                    </div>
                    <div className="space-y-2" data-id="element-534">
                      {generatedStrategy.subjectLines.map((line, idx) => <div key={idx} className="flex items-center gap-2 group" data-id="element-535">
                          <div className="flex-1 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md" data-id="element-536">
                            {line}
                          </div>
                          <button onClick={() => handleUseSubjectLine(line)} className="px-3 py-1.5 text-xs font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors whitespace-nowrap" data-id="element-537">
                            Use This
                          </button>
                          <button onClick={() => handleCopyToClipboard(line, `subject-${idx}`)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors" data-id="element-538">
                            {copiedItem === `subject-${idx}` ? <Check className="h-4 w-4 text-green-500" data-id="element-539" /> : <Copy className="h-4 w-4" data-id="element-540" />}
                          </button>
                        </div>)}
                    </div>
                  </div>

                  {/* Key Messages */}
                  <div className="bg-white rounded-lg p-4 border border-purple-100" data-id="element-541">
                    <div className="flex items-center gap-2 text-purple-700 font-medium mb-3" data-id="element-542">
                      <Zap className="h-4 w-4" data-id="element-543" />
                      Key Messages to Include
                    </div>
                    <ul className="space-y-2" data-id="element-544">
                      {generatedStrategy.keyMessages.map((msg, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-545">
                          <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-546" />
                          {msg}
                        </li>)}
                    </ul>
                  </div>

                  {/* Two Column Layout */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-547">
                    {/* Call to Actions */}
                    <div className="bg-white rounded-lg p-4 border border-purple-100" data-id="element-548">
                      <div className="flex items-center gap-2 text-purple-700 font-medium mb-3" data-id="element-549">
                        <TrendingUp className="h-4 w-4" data-id="element-550" />
                        Call-to-Action Ideas
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-551">
                        {generatedStrategy.callToActions.map((cta, idx) => <span key={idx} className="inline-flex px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-full" data-id="element-552">
                            {cta}
                          </span>)}
                      </div>
                    </div>

                    {/* Emotional Triggers */}
                    <div className="bg-white rounded-lg p-4 border border-purple-100" data-id="element-553">
                      <div className="flex items-center gap-2 text-purple-700 font-medium mb-3" data-id="element-554">
                        <Heart className="h-4 w-4" data-id="element-555" />
                        Emotional Triggers
                      </div>
                      <ul className="space-y-1.5" data-id="element-556">
                        {generatedStrategy.emotionalTriggers.map((trigger, idx) => <li key={idx} className="text-sm text-gray-700 flex items-start gap-2" data-id="element-557">
                              <span className="text-purple-400" data-id="element-558">•</span>
                              {trigger}
                            </li>)}
                      </ul>
                    </div>
                  </div>

                  {/* Timing & Personalization */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-559">
                    <div className="bg-white rounded-lg p-4 border border-purple-100" data-id="element-560">
                      <div className="flex items-center gap-2 text-purple-700 font-medium mb-3" data-id="element-561">
                        <Clock className="h-4 w-4" data-id="element-562" />
                        Timing Recommendations
                      </div>
                      <ul className="space-y-1.5" data-id="element-563">
                        {generatedStrategy.timingTips.map((tip, idx) => <li key={idx} className="text-sm text-gray-700 flex items-start gap-2" data-id="element-564">
                            <span className="text-purple-400" data-id="element-565">•</span>
                            {tip}
                          </li>)}
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-purple-100" data-id="element-566">
                      <div className="flex items-center gap-2 text-purple-700 font-medium mb-3" data-id="element-567">
                        <Users className="h-4 w-4" data-id="element-568" />
                        Personalization Tips
                      </div>
                      <ul className="space-y-1.5" data-id="element-569">
                        {generatedStrategy.personalizationTips.map((tip, idx) => <li key={idx} className="text-sm text-gray-700 flex items-start gap-2" data-id="element-570">
                              <span className="text-purple-400" data-id="element-571">•</span>
                              {tip}
                            </li>)}
                      </ul>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Campaign Details */}
            <div className="grid grid-cols-2 gap-6" data-id="element-572">
              <div className="col-span-2" data-id="element-573">
                <label className="block text-sm font-medium text-gray-700" data-id="element-574">
                  Campaign Name (Internal)
                </label>
                <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.name} onChange={e => setCampaign({
              ...campaign,
              name: e.target.value
            })} data-id="element-575" />
              </div>
              <div className="col-span-2" data-id="element-576">
                <label className="block text-sm font-medium text-gray-700" data-id="element-577">
                  Email Subject
                  {campaign.subject && generatedStrategy?.subjectLines.includes(campaign.subject) && <span className="ml-2 text-xs text-purple-600 font-normal" data-id="element-578">
                        ✨ Using generated suggestion
                      </span>}
                </label>
                <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.subject} onChange={e => setCampaign({
              ...campaign,
              subject: e.target.value
            })} data-id="element-579" />
              </div>
              <div className="col-span-2" data-id="element-580">
                <label className="block text-sm font-medium text-gray-700" data-id="element-581">
                  Preheader Text
                </label>
                <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.preheader} onChange={e => setCampaign({
              ...campaign,
              preheader: e.target.value
            })} data-id="element-582" />
              </div>
              <div data-id="element-583">
                <label className="block text-sm font-medium text-gray-700" data-id="element-584">
                  From Name
                </label>
                <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.fromName} onChange={e => setCampaign({
              ...campaign,
              fromName: e.target.value
            })} data-id="element-585" />
              </div>
              <div data-id="element-586">
                <label className="block text-sm font-medium text-gray-700" data-id="element-587">
                  From Email
                </label>
                <input type="email" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.fromEmail} onChange={e => setCampaign({
              ...campaign,
              fromEmail: e.target.value
            })} data-id="element-588" />
              </div>

              <div className="col-span-2" data-id="element-589">
                <div className="border-b border-gray-200 mb-4" data-id="element-590">
                  <nav className="-mb-px flex gap-4" data-id="element-591">
                    <button type="button" onClick={() => setContentTab('html')} className={`py-2 px-1 border-b-2 text-sm font-medium ${contentTab === 'html' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-592">
                      HTML Content
                    </button>
                    <button type="button" onClick={() => setContentTab('text')} className={`py-2 px-1 border-b-2 text-sm font-medium ${contentTab === 'text' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-593">
                      Plain Text
                    </button>
                  </nav>
                </div>

                {contentTab === 'html' ? <CodeEditor label="Email HTML Content" language="html" className="min-h-[300px]" value={campaign.htmlContent} onChange={e => setCampaign({
              ...campaign,
              htmlContent: e.target.value
            })} data-id="element-594" /> : <div className="space-y-2" data-id="element-595">
                    <label className="block text-sm font-medium text-gray-900" data-id="element-596">
                      Plain Text Version
                    </label>
                    <p className="text-xs text-gray-500" data-id="element-597">
                      For email clients that don't support HTML.
                    </p>
                    <textarea rows={12} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono" placeholder="Enter plain text version of your email..." value={campaign.textContent} onChange={e => setCampaign({
                ...campaign,
                textContent: e.target.value
              })} data-id="element-598" />
                  </div>}
              </div>
            </div>
          </div>}

        {currentStep === 3 && <div className="space-y-6" data-id="element-599">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-600">
              Review Campaign
            </h2>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-601">
              <div className="grid grid-cols-2 gap-4" data-id="element-602">
                <div data-id="element-603">
                  <span className="text-sm text-gray-500 block" data-id="element-604">Subject</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-605">
                    {campaign.subject}
                  </span>
                </div>
                <div data-id="element-606">
                  <span className="text-sm text-gray-500 block" data-id="element-607">From</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-608">
                    {campaign.fromName} &lt;{campaign.fromEmail}&gt;
                  </span>
                </div>
                <div data-id="element-609">
                  <span className="text-sm text-gray-500 block" data-id="element-610">
                    Target Lists
                  </span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-611">
                    {campaign.targetNewsletters.length} lists selected
                  </span>
                </div>
                <div data-id="element-612">
                  <span className="text-sm text-gray-500 block" data-id="element-613">
                    Sub-Regions
                  </span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-614">
                    {campaign.subRegions.length > 0 ? campaign.subRegions.join(', ') : 'All Regions'}
                  </span>
                </div>
                {campaign.targetTags.length > 0 && <div className="col-span-2" data-id="element-615">
                    <span className="text-sm text-gray-500 block" data-id="element-616">
                      Tag Filters
                    </span>
                    <div className="flex flex-wrap gap-1 mt-1" data-id="element-617">
                      {campaign.targetTags.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700" data-id="element-618">
                          <Tag className="h-3 w-3" data-id="element-619" />
                          {tag}
                        </span>)}
                    </div>
                  </div>}
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4" data-id="element-620">
              <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-621">
                Compliance Checklist
              </h3>
              <div className="space-y-2" data-id="element-622">
                <div className="flex items-center gap-2 text-sm text-green-700" data-id="element-623">
                  <CheckCircle2 className="h-4 w-4" data-id="element-624" />
                  Sending to confirmed subscribers only
                </div>
                <div className="flex items-center gap-2 text-sm text-green-700" data-id="element-625">
                  <CheckCircle2 className="h-4 w-4" data-id="element-626" />
                  Unsubscribe link included automatically
                </div>
              </div>
            </div>
          </div>}

        {currentStep === 4 && !exportResult && <div className="space-y-6" data-id="element-627">
            <div className="text-center py-4" data-id="element-628">
              <div className="mx-auto h-16 w-16 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-629">
                <Database className="h-8 w-8 text-purple-600" data-id="element-630" />
              </div>
              <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-631">
                Export to Zoho Campaigns
              </h2>
              <p className="mt-2 text-gray-500" data-id="element-632">
                This will mark <strong data-id="element-633">{getTargetEmails().length}</strong>{' '}
                contacts in Zoho CRM for campaign delivery.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-634">
              <h3 className="text-sm font-medium text-blue-900 mb-3 flex items-center gap-2" data-id="element-635">
                <Database className="h-4 w-4" data-id="element-636" />
                Send Bucket Configuration
              </h3>
              <div className="space-y-3" data-id="element-637">
                <div className="flex items-center justify-between" data-id="element-638">
                  <span className="text-sm text-blue-700" data-id="element-639">Bucket ID:</span>
                  <code className="text-sm font-mono bg-white px-2 py-1 rounded border border-blue-200 text-blue-900" data-id="element-640">
                    {sendBucketConfig.bucketId}
                  </code>
                </div>
                <div className="flex items-center justify-between" data-id="element-641">
                  <span className="text-sm text-blue-700" data-id="element-642">Contacts:</span>
                  <span className="text-sm font-medium text-blue-900" data-id="element-643">
                    {getTargetEmails().length}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3" data-id="element-644">
              <label className="block text-sm font-medium text-gray-700" data-id="element-645">
                <Clock className="inline h-4 w-4 mr-1" data-id="element-646" />
                Bucket Expiry (Optional)
              </label>
              <p className="text-xs text-gray-500" data-id="element-647">
                Automatically clear this bucket after the specified time.
              </p>
              <div className="grid grid-cols-4 gap-3" data-id="element-648">
                {[null, 24, 48, 72].map(hours => <button key={hours || 'none'} type="button" onClick={() => setSendBucketConfig(prev => ({
              ...prev,
              expiryHours: hours
            }))} className={`px-4 py-2 text-sm font-medium rounded-md border ${sendBucketConfig.expiryHours === hours ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-id="element-649">
                    {getExpiryLabel(hours)}
                  </button>)}
              </div>
            </div>

            {showConflictWarning && bucketConflicts?.conflicts.length > 0 && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-650">
                <div className="flex items-start gap-3" data-id="element-651">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-652" />
                  <div className="flex-1" data-id="element-653">
                    <h4 className="text-sm font-medium text-yellow-900" data-id="element-654">
                      Active Bucket Conflicts Detected
                    </h4>
                    <p className="text-sm text-yellow-700 mt-1" data-id="element-655">
                      {bucketConflicts.conflicts.length} contact(s) already have
                      active Send Buckets. Proceeding will overwrite their
                      existing buckets.
                    </p>
                    <details className="mt-2" data-id="element-656">
                      <summary className="text-xs text-yellow-700 cursor-pointer hover:text-yellow-900" data-id="element-657">
                        View conflicts
                      </summary>
                      <div className="mt-2 space-y-1 max-h-32 overflow-y-auto" data-id="element-658">
                        {bucketConflicts.conflicts.slice(0, 10).map((conflict: any) => <div key={conflict.email} className="text-xs text-yellow-700 font-mono" data-id="element-659">
                              {conflict.email} → {conflict.existingBucket}
                            </div>)}
                        {bucketConflicts.conflicts.length > 10 && <div className="text-xs text-yellow-700" data-id="element-660">
                            ... and {bucketConflicts.conflicts.length - 10} more
                          </div>}
                      </div>
                    </details>
                  </div>
                </div>
              </div>}

            {zohoCrmService.isMockMode() && <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-661">
                <p className="text-xs text-gray-600" data-id="element-662">
                  <strong data-id="element-663">Demo Mode:</strong> Running in mock mode. No actual
                  CRM updates will be made.
                </p>
              </div>}

            <div className="flex justify-center gap-4 pt-4" data-id="element-664">
              <button onClick={() => setCurrentStep(3)} className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50" data-id="element-665">
                Back to Review
              </button>
              <button onClick={handleExportToZoho} disabled={isExporting || !campaign.name || !campaign.subject} className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-666">
                {isExporting ? <>
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" data-id="element-667" />
                    Exporting...
                  </> : <>
                    <ExternalLink className="h-5 w-5" data-id="element-668" />
                    Export to Zoho CRM
                  </>}
              </button>
            </div>
          </div>}

        {currentStep === 4 && exportResult && <div className="py-8 space-y-6" data-id="element-669">
            {exportResult.success ? <>
                <div className="text-center" data-id="element-670">
                  <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center" data-id="element-671">
                    <CheckCircle2 className="h-8 w-8 text-green-600" data-id="element-672" />
                  </div>
                  <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-673">
                    Audience exported. Syncing to Zoho Campaigns…
                  </h2>
                  <p className="mt-2 text-gray-600" data-id="element-674">
                    <strong data-id="element-675">{exportResult.updatedCount}</strong> contacts marked
                    in Zoho CRM
                  </p>
                </div>

                {exportResult.warnings.length > 0 && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-2xl mx-auto" data-id="element-676">
                    <p className="text-sm font-medium text-yellow-900" data-id="element-677">
                      Warnings:
                    </p>
                    <ul className="mt-2 text-sm text-yellow-700 space-y-1" data-id="element-678">
                      {exportResult.warnings.map((warning: string, i: number) => <li key={i} data-id="element-679">• {warning}</li>)}
                    </ul>
                  </div>}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto" data-id="element-680">
                  <h3 className="text-base font-semibold text-blue-900 mb-4" data-id="element-681">
                    Next steps
                  </h3>
                  <ol className="space-y-4 text-sm text-blue-900" data-id="element-682">
                    <li className="flex gap-3" data-id="element-683">
                      <span className="font-semibold flex-shrink-0" data-id="element-684">1.</span>
                      <div data-id="element-685">
                        <p className="font-medium" data-id="element-686">Go to Zoho Campaigns</p>
                        <a href="https://campaigns.zoho.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-blue-700 hover:text-blue-800 underline" data-id="element-687">
                          Open Zoho Campaigns
                          <ExternalLink className="h-3 w-3" data-id="element-688" />
                        </a>
                      </div>
                    </li>
                    <li className="flex gap-3" data-id="element-689">
                      <span className="font-semibold flex-shrink-0" data-id="element-690">2.</span>
                      <div className="flex-1" data-id="element-691">
                        <p className="font-medium mb-2" data-id="element-692">
                          Create a Segment with the filter:
                        </p>
                        <div className="bg-white border border-blue-300 rounded p-3 font-mono text-sm" data-id="element-693">
                          <div className="flex items-center justify-between gap-4" data-id="element-694">
                            <code className="text-blue-900" data-id="element-695">
                              Send_Bucket_ID equals {sendBucketConfig.bucketId}
                            </code>
                            <button onClick={() => navigator.clipboard.writeText(`Send_Bucket_ID equals ${sendBucketConfig.bucketId}`)} className="flex-shrink-0 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200" title="Copy filter" data-id="element-696">
                              Copy
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                    <li className="flex gap-3" data-id="element-697">
                      <span className="font-semibold flex-shrink-0" data-id="element-698">3.</span>
                      <div data-id="element-699">
                        <p className="font-medium" data-id="element-700">
                          Send your campaign through Zoho Campaigns using that
                          segment
                        </p>
                      </div>
                    </li>
                  </ol>

                  <div className="mt-4 pt-4 border-t border-blue-200" data-id="element-701">
                    <div className="flex items-start gap-2 text-sm text-blue-800" data-id="element-702">
                      <Clock className="h-4 w-4 flex-shrink-0 mt-0.5" data-id="element-703" />
                      <p data-id="element-704">
                        <strong data-id="element-705">Note:</strong> This may take a few minutes to
                        appear due to Zoho CRM sync.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-w-2xl mx-auto" data-id="element-706">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-707">
                    What to expect
                  </h3>
                  <div className="space-y-3 text-sm text-gray-700" data-id="element-708">
                    <p data-id="element-709">
                      <strong data-id="element-710">Wait 5–10 minutes</strong>, then refresh Zoho
                      Campaigns to see your segment.
                    </p>
                    <p data-id="element-711">
                      If the audience does not appear after{' '}
                      <strong data-id="element-712">20–30 minutes</strong>:
                    </p>
                    <ul className="ml-5 space-y-1 list-disc text-gray-600" data-id="element-713">
                      <li data-id="element-714">Check Zoho CRM → Campaigns sync status</li>
                      <li data-id="element-715">Confirm Campaigns contact sync settings</li>
                    </ul>
                    <p className="text-xs text-gray-500 italic mt-3" data-id="element-716">
                      This is normal behavior for Zoho CRM and Campaigns.
                    </p>
                  </div>
                </div>

                <div className="text-center pt-4" data-id="element-717">
                  <button onClick={() => navigate('/campaigns')} className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-718">
                    Return to Campaigns
                  </button>
                </div>
              </> : <>
                <div className="text-center" data-id="element-719">
                  <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center" data-id="element-720">
                    <X className="h-8 w-8 text-red-600" data-id="element-721" />
                  </div>
                  <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-722">
                    Export Failed
                  </h2>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto" data-id="element-723">
                  <p className="text-sm font-medium text-red-900 mb-2" data-id="element-724">
                    Errors:
                  </p>
                  <ul className="text-sm text-red-700 space-y-1" data-id="element-725">
                    {exportResult.errors.map((error: string, i: number) => <li key={i} data-id="element-726">• {error}</li>)}
                  </ul>
                </div>
                <div className="text-center" data-id="element-727">
                  <button onClick={() => setExportResult(null)} className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-728">
                    Try Again
                  </button>
                </div>
              </>}
          </div>}

        {currentStep < 4 && <div className="mt-8 flex justify-between pt-6 border-t border-gray-100" data-id="element-729">
            <button onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-730">
              Back
            </button>

            <button onClick={() => setCurrentStep(Math.min(4, currentStep + 1))} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-731">
              Next Step
              <ArrowRight className="h-4 w-4" data-id="element-732" />
            </button>
          </div>}
      </div>
    </div>;
}